import { ReactNode, useCallback, useMemo } from 'react'

import DateFnsUtils from '@date-io/date-fns'
import { Box, TextField, TextFieldProps } from '@material-ui/core'
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import { KeyboardDatePickerProps } from '@material-ui/pickers'
// @ts-expect-error untyped package
import { isCredibleDate } from '@scoir/date'

import generateUniqueId from 'common/generate-unique-id'

const CustomErrorText = ({ id, children }: { id: string; children: ReactNode }) => {
  return (
    <Box
      id={id}
      role="alert"
      aria-live="polite"
      component="span"
      m={0}
    >
      {children}
    </Box>
  )
}

type Props = Omit<KeyboardDatePickerProps, 'value'> & {
  date?: Date | string | null
  invalidDateMessage?: string
  minDateMessage?: string
  maxDateMessage?: string
  pickerIdSuffix: string
}

const DatePicker = ({
  date,
  invalidDateMessage = 'Invalid Date Format',
  minDateMessage = 'Date should not be before minimal date',
  maxDateMessage = 'Date should not be after maximal date',
  pickerIdSuffix,
  ...datePickerProps
}: Props) => {
  const value = isCredibleDate(date) ? date : null

  const uniqueId = useMemo(() => generateUniqueId(), [])
  const invalidErrorTextId = `date-invalid-error-id-${uniqueId}`
  const minErrorTextId = `date-min-error-id-${uniqueId}`
  const maxErrorTextId = `date-max-error-id-${uniqueId}`

  const CustomTextField = useCallback(
    (textFieldProps: TextFieldProps) => {
      const inputProps = {
        'aria-describedby': `${invalidErrorTextId} ${minErrorTextId} ${maxErrorTextId}`,
      }
      return (
        <TextField
          {...textFieldProps}
          inputProps={{
            form: { autoComplete: 'off' },
            margin: 'normal',
            ...inputProps,
            ...textFieldProps.inputProps,
          }}
          margin="none"
          fullWidth
        />
      )
    },
    [invalidErrorTextId, minErrorTextId, maxErrorTextId],
  )

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <KeyboardDatePicker
        {...datePickerProps}
        value={value}
        autoOk
        variant="inline"
        inputVariant="outlined"
        KeyboardButtonProps={{ 'aria-label': 'change date' }}
        id={`${pickerIdSuffix}-date-picker`}
        TextFieldComponent={CustomTextField}
        invalidDateMessage={<CustomErrorText id={invalidErrorTextId}>{invalidDateMessage}</CustomErrorText>}
        minDateMessage={<CustomErrorText id={minErrorTextId}>{minDateMessage}</CustomErrorText>}
        maxDateMessage={<CustomErrorText id={maxErrorTextId}>{maxDateMessage}</CustomErrorText>}
      />
    </MuiPickersUtilsProvider>
  )
}

export default DatePicker
